{
  "description": "Original static display copy for issue #2311. None of these passages is an excerpt from Continue Without Me or a live generation. IDs are composed by `fixtureId` in series-demo.tsx from the picker's own choices. Highlight text is an exact substring of a paragraph. Use a visible Illustrative snippet label.",
  "fixtures": [
    {
      "id": "cyberpunk-agents-business",
      "title": "The first customer",
      "make": "Story",
      "genre": "Cyberpunk",
      "topicBundle": "AI + business",
      "brief": "A founder brings a dazzling demo to a noodle shop just as lunch goes wrong.",
      "topics": [
        "AI agents",
        "Building a business"
      ],
      "paragraphs": [
        "Nia's sales demo filled the noodle shop with holographic koi. They swam through the steam, arranged themselves into revenue charts, and passed straight through a delivery rider who was shouting about the missing dumplings. Nobody looked at a single chart.",
        "Behind the counter, Sol lifted an empty bamboo steamer. “Can your fish warn me before this happens?” Nia swept three weeks of beautiful dashboards off the display. Her agent checked the stock sheet and prepared a rush order. It could draft the purchase; only Nia could send it.",
        "She corrected the quantity, tapped Send, and pulled up a much smaller demo: tomorrow's shortages, before the lunch rush. Sol leaned over to read it. For the first time all morning, he stopped shouting. One forgotten koi drifted through his head."
      ],
      "highlights": [
        {
          "text": "It could draft the purchase; only Nia could send it.",
          "topic": "AI agents",
          "annotation": "This fictional agent can prepare a purchase without having authority to place it."
        },
        {
          "text": "tomorrow's shortages, before the lunch rush",
          "topic": "Building a business",
          "annotation": "The customer's immediate problem changes what Nia offers to build."
        }
      ]
    },
    {
      "id": "fantasy-agents-business",
      "title": "The brass assistant",
      "make": "Story",
      "genre": "Fantasy",
      "topicBundle": "AI + business",
      "brief": "An enchanted sales demonstration goes badly until the craftswoman notices what the baker needs.",
      "topics": [
        "Delegation and agents",
        "Building a business"
      ],
      "paragraphs": [
        "Iona's brass assistant bowed so deeply that its hat fell into the dough. Behind it, their magnificent flour sorter separated a sack into seven grades of fineness. The baker watched a mouse carry off the sample marked Exquisite.",
        "He didn't need finer flour. He needed to know when the miller had delivered six sacks instead of ten, before he lit the ovens and promised bread to half the town. Iona looked at the four empty hooks by the loading door. She could have asked about those an hour ago.",
        "She folded away the sorter's brochure. The assistant drafted a smaller bargain: count the delivery each dawn and ring the baker's bell if anything was missing. It could write the terms; only Iona's seal could bind her to them. The baker offered a week's trial. His first condition was getting the hat out of his dough."
      ],
      "highlights": [
        {
          "text": "It could write the terms; only Iona's seal could bind her to them.",
          "topic": "Delegation and agents",
          "annotation": "A fantasy analogy for delegation: preparing a commitment and authorizing it are separate powers."
        },
        {
          "text": "count the delivery each dawn and ring the baker's bell if anything was missing",
          "topic": "Building a business",
          "annotation": "The smaller service addresses the buyer's actual problem. The trial gives them a way to test it."
        }
      ]
    },
    {
      "id": "course-agents-business",
      "title": "Build the smallest useful service",
      "make": "Audio course",
      "genre": null,
      "topicBundle": "AI + business",
      "brief": "A noodle-shop disaster makes a useful first lesson in agents and customer discovery.",
      "topics": [
        "AI agents",
        "Customer discovery"
      ],
      "paragraphs": [
        "Picture a noodle shop at noon. The queue reaches the bicycle rack, the printer is coughing out tickets, and someone has just announced that there are no dumplings. You've arrived to sell the owner a beautiful dashboard. He has one question: can it warn him before lunch disappears?",
        "That gives you a smaller service to try: tomorrow's shortage list, ready before he orders. In our example, an agent reads the stock sheet and drafts a shopping list. It has no permission to place the order. The owner checks the quantities first, which is useful if someone has recorded six dumplings as six crates.",
        "Try the list for a few mornings. Did it flag the shortages? Did the owner use it? Those answers give you something firmer to build on than his polite nod at your dashboard."
      ],
      "highlights": [
        {
          "text": "It has no permission to place the order.",
          "topic": "AI agents",
          "annotation": "This hypothetical workflow requires controls that separate drafting from ordering."
        },
        {
          "text": "Did it flag the shortages? Did the owner use it?",
          "topic": "Customer discovery",
          "annotation": "Test whether the small service solves this customer's problem before expanding it."
        }
      ]
    },
    {
      "id": "cyberpunk-cooking",
      "title": "Dinner above the blackout",
      "make": "Story",
      "genre": "Cyberpunk",
      "topicBundle": "Cooking",
      "brief": "The power is out, six neighbors are hungry, and one rooftop hotplate still works.",
      "topics": [
        "Cooking"
      ],
      "paragraphs": [
        "Mika had promised dinner for six before the blackout took out the lifts. Now everyone was on the roof, the city below them blinking through a restart, and her borrowed hotplate was running off a scooter battery. She tipped the entire bowl of sliced mushrooms into the pan. A pale puddle spread beneath them. So much for the sizzling entrance.",
        "She scooped half into a bowl and let the remaining mushrooms cook until the puddle disappeared. Given room and a little time, their edges began to brown. The smell brought her upstairs neighbor out from behind the satellite dish, still holding the emergency crackers he'd brought in case she failed.",
        "Mika cooked the second batch, tossed everything with noodles, and passed the bowls along the parapet. Below them, a billboard flickered back to life with an advertisement for instant luxury dining. Her neighbor used his last cracker to scrape the pan."
      ],
      "highlights": [
        {
          "text": "She scooped half into a bowl and let the remaining mushrooms cook until the puddle disappeared.",
          "topic": "Cooking",
          "annotation": "Mushrooms release moisture as they cook. Smaller batches give that moisture room to evaporate before browning."
        }
      ]
    },
    {
      "id": "fantasy-cooking",
      "title": "The soup before the feast",
      "make": "Story",
      "genre": "Fantasy",
      "topicBundle": "Cooking",
      "brief": "An apprentice cook has one pot of disappointing soup and a dragon waiting for seconds.",
      "topics": [
        "Cooking"
      ],
      "paragraphs": [
        "The kitchen dragon had eaten the garnish again. Nell found it asleep in the parsley basket, one green leaf stuck to its snout, while the feast bell began its first warning. Her lentil soup was thick, hot, and disappointingly dull. Even the dragon had stopped asking for a taste.",
        "Nell reached for the salt, then noticed the cut lemon beside the chopping board. She squeezed a few drops into a spoonful of soup and tasted it before changing the whole pot. Better. She added a little lemon juice to the soup, stirred, and tasted again. This time she wanted another spoonful.",
        "The dragon woke at the scrape of the ladle. Nell filled the tureen and carried it toward the hall, leaving a small bowl to keep it occupied. Behind her came a satisfied rumble, then the crunch of the parsley basket. She would have to explain that to the head cook after dinner."
      ],
      "highlights": [
        {
          "text": "She squeezed a few drops into a spoonful of soup and tasted it before changing the whole pot.",
          "topic": "Cooking",
          "annotation": "Try a seasoning change in a small sample first. Nell uses lemon juice to see whether a little acidity improves this soup."
        }
      ]
    },
    {
      "id": "course-cooking",
      "title": "Give the mushrooms some room",
      "make": "Audio course",
      "genre": null,
      "topicBundle": "Cooking",
      "brief": "One crowded pan gives you a useful lesson in moisture, browning, and when to cook in batches.",
      "topics": [
        "Cooking"
      ],
      "paragraphs": [
        "You've sliced the mushrooms, heated the pan, and tipped in the lot. Dinner should smell wonderful about now. Instead, the mushrooms are sitting in a gray puddle. They release water as they cook, and your crowded pan has become a very small mushroom swimming pool.",
        "For browned mushrooms, try two batches. Spread the first batch in a single layer with a little oil and let the released water cook away. Once the pan is no longer wet, give the mushrooms time to develop brown patches, turning them occasionally. Watch the food as well as the clock; pan size and heat will change how long this takes.",
        "Move that batch to a plate, cook the rest, then combine them. Taste a piece before adding anything else. Next, try a little salt on one piece and a few drops of lemon on another. You've got three small comparisons to make, and dinner to put them in."
      ],
      "highlights": [
        {
          "text": "For browned mushrooms, try two batches.",
          "topic": "Cooking",
          "annotation": "Less crowding helps moisture escape. Browning becomes easier once the pan is no longer wet."
        },
        {
          "text": "Watch the food as well as the clock; pan size and heat will change how long this takes.",
          "topic": "Cooking",
          "annotation": "Use visible changes to judge progress instead of relying on a fixed cooking time."
        }
      ]
    },
    {
      "id": "cyberpunk-fiction",
      "title": "The voice at the station",
      "make": "Story",
      "genre": "Cyberpunk",
      "topicBundle": "No learning topics",
      "brief": "A courier chasing the last train hears a familiar voice from a disconnected speaker.",
      "topics": [],
      "paragraphs": [
        "Sera vaulted the station barrier with a stolen birthday cake under one arm and her last fare clenched between her teeth. The train was still there. For once, the city had left her something she could catch.",
        "Then the dead speaker above the kiosk said her name. She stopped so hard that a sugar astronaut slid off the cake. Two cut wires dangled beneath the speaker. Three years ago, she'd held the ladder while her sister disconnected them. Two years ago, her sister had vanished.",
        "“Miss this one.” The voice was unmistakable. Behind Sera, the train doors began to chime. In her coat pocket, the parcel addressed in her sister's handwriting grew warm. She put the cake on the platform and backed away from the doors. Whatever happened next, she wanted both hands free."
      ],
      "highlights": []
    },
    {
      "id": "fantasy-fiction",
      "title": "The unclaimed dragon",
      "make": "Story",
      "genre": "Fantasy",
      "topicBundle": "No learning topics",
      "brief": "A stable keeper needs to survive an inspection. Her smallest dragon has other plans.",
      "topics": [],
      "paragraphs": [
        "Tavi was halfway through hiding the scorch marks when the smallest dragon sneezed a royal seal into her breakfast. It landed crown-side up in the porridge. On the stairs, the inspector's boots began their slow, expensive climb.",
        "She grabbed the dragon under its warm little belly. Two brass buckles and a page of the breeding register fell out of its nest. Beneath them lay a letter addressed to her. If you are reading this, it began, your mother has failed to kill me.",
        "Tavi read the sentence twice. Her mother sold jam. The latch lifted, and she stuffed the letter into her shirt. The dragon scrambled onto her shoulder, puffed itself up, and greeted the inspector with a perfect imitation of the queen's voice. “You're late.”"
      ],
      "highlights": []
    }
  ]
}
